/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  Flex,
  Text,
  Tooltip,
  ScrollArea,
  Switch,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import ImageObserved from "../Image";
import { PromptField } from "./PromptField";
import { PipelineParameters } from "../Pipeline";
import { priceMap } from "../Router";
import { useEffect, useRef, useState } from "react";
import CanvasDraw from "react-canvas-draw";
import { useRequest } from "ahooks";
import { NOTIFICATION_SERVICE } from "../../../../components/notifications/NotificationService";

const PROMPT_ENHANCEMENT_TEXT = `
Extend this scene into a wide, open area suitable for a commercial automotive range shot. Add an even, unobstructed surface in the foreground, blending seamlessly with the existing terrain. Maintain realistic ground texture scale – small gravel, grass, snow, or dirt in the foreground must appear correctly sized relative to three standing cars. Avoid exaggerating surface detail. Place three static generic vehicles spaced evenly in a shallow V-formation, viewed from a moderately elevated camera (about 1.5 meters high) looking slightly downward. Ensure the overall scene remains consistent in lighting, depth, and material continuity with the original image. Do not reinterpret small pebbles or ground details as oversized elements. Respect the original surface structure and perspective.`;

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore, appStore },
  } = useStore();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedModel, setSelectedModel] = useState<'nanoBanana' | 'kontext'>('nanoBanana');
  const [inpaintMode, setInpaintMode] = useState(false);
  const [brushRadius, setBrushRadius] = useState(20);
  const [brushOpacity, setBrushOpacity] = useState(0.7);
  const [promptEnhancements, setPromptEnhancements] = useState(true);
  const renderRef = useRef<CanvasDraw>(null);
  const [canvasHeight, setCanvasHeight] = useState(1024);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [extraImages, setExtraImages] = useState<string[]>([]);
  const [selectedSize, setSelectedSize] = useState<'1K' | '2K' | '4K'>('4K');
  const sizeMultiplier = selectedSize === '1K' ? 1 : selectedSize === '2K' ? 2 : 4;
  const baseCredits = priceMap.AI_RETOUCH_2;
  const computedCredits = baseCredits * sizeMultiplier;

  const images = [
    'https://cdn.maground.ai/eu/workspace/ma-CSC_1715_1_163093_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_2063_1_204279_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_2074_1_204356_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_2296_1_214003_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3032_1_264020_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3282_1_291240_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3333_1_291610_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3349_1_291734_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3530_1_361496_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3541_1_361562_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3565_1_364721_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3566_1_364727_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3568_1_364745_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3739_1_584494_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3784_1_650100_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3795_1_650183_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_3979_1_728038_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_634_1_93975_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-CSC_914_1_116747_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-ELI_360_1_105548_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBE_819_1_348318_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBE_839_1_348533_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBE_841_1_348549_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBE_989_1_358084_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBO_192_1_96019_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBO_2504_1_339586_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-JBO_2954_1_400046_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-LZI_385_1_84494_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-LZI_641_1_179381_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_11767_1_577946_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_11821_1_578427_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_12058_1_590668_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_12060_1_590678_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_12062_1_590689_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_2224_1_157624_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_2950_1_180027_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_2989_1_180602_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_3088_1_183736_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_3112_1_183962_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_4797_1_244575_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_6746_1_329100_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_6753_1_329189_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_6754_1_329196_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_8375_1_427952_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_8421_1_428252_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_8427_1_428308_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_8428_1_428314_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_8436_1_428347_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAG_9602_1_490236_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAL_293_1_651619_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MAX_2233_1_424512_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_2383_1_150219_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_2406_1_150454_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_2602_1_152500_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_2606_1_152549_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_4276_1_425781_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_4282_1_425857_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_4291_1_425972_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MHA_4293_1_426003_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MJU_58_1_441304_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MLT_1828_1_661508_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MLT_1830_1_661537_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-MWE_173_1_80388_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PHA_177_1_380007_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PHA_1984_1_594308_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PHA_2145_1_595642_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PHA_902_1_491363_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PSC_1030_1_634564_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-PSC_54_1_114589_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-RGA_15_1_660641_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-RNA_1256_1_420877_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-RNA_1258_1_420889_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-RNA_2496_1_491846_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-RNA_5631_1_613030_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-SAR_190_1_593832_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-SAR_76_1_589860_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-STC_214_1_656404_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-STC_248_1_657498_L.jpg',
    'https://cdn.maground.ai/eu/workspace/ma-STC_287_1_672988_L.jpg',
  ];
  const baseImageSrc = spaceStore.selectedModel?.mainImage || spaceStore.selectedImage || images[0];

  const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  const composeWithBackground = async (minLongSide: number = 1200): Promise<string | null> => {
    if (!renderRef.current) return null;
    try {
      const strokesDataUrl = (renderRef.current as any).getDataURL('image/png', false);
      const bgSrc = baseImageSrc;
      const [bgImg, strokesImg] = await Promise.all([
        loadImage(bgSrc),
        loadImage(strokesDataUrl),
      ]);

      const canvas = document.createElement('canvas');
      const currentRatio = canvasWidth / canvasHeight;
      const targetLongSide = Math.max(minLongSide, Math.max(canvasWidth, canvasHeight));
      if (currentRatio >= 1) {
        canvas.width = targetLongSide;
        canvas.height = Math.round(targetLongSide / currentRatio);
      } else {
        canvas.height = targetLongSide;
        canvas.width = Math.round(targetLongSide * currentRatio);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
      ctx.drawImage(strokesImg, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } catch (_e) {
      return null;
    }
  };

  // const handleDownloadComposite = async () => {
  //   try {
  //     const composed = await composeWithBackground(1200);
  //     const dataUrl = composed || (renderRef.current as any)?.getDataURL('image/png', true);
  //     if (!dataUrl) return;
  //     const link = document.createElement('a');
  //     link.href = dataUrl;
  //     link.download = 'screenshot.png';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (_e) {
  //     // noop
  //   }
  // };
  const handleSelectBaseImage = (src: string) => {
    if (src === baseImageSrc) return;
    spaceStore.setSelectedImage({ src });
    renderRef.current?.clear();
    setExtraImages([]);
  };

  // Convert base64 to remote URL
  const { runAsync: runAsyncConvertBase64 } = useRequest(
    async (model: { url: string }) => appStore.convertImageBase64({ url: model.url }),
    {
      manual: true,
      onSuccess: (res) => {
        if ((res as any).error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        }
        return (res as any).url;
      },
    }
  );

  // Adjust canvas to image ratio
  useEffect(() => {
    const src = baseImageSrc;
    if (!src) return;
    const img = new Image();
    img.onload = function () {
      const ratio = img.width / img.height;
      if (img.width > img.height) {
        setCanvasWidth(800);
        setCanvasHeight(800 / ratio);
      } else {
        setCanvasHeight(1024);
        setCanvasWidth(1024 * ratio);
      }
    };
    img.src = src;
  }, [baseImageSrc]);

  const handleAddExtraImages = async (files: FileList | null) => {
    if (!files) return;
    const remaining = 5 - extraImages.length;
    const toProcess = Array.from(files).slice(0, remaining);
    for (const file of toProcess) {
      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      try {
        const { url } = await runAsyncConvertBase64({ url: base64 }) as unknown as { url: string };
        setExtraImages((prev) => [...prev, url]);
      } catch (e) {
        NOTIFICATION_SERVICE.errorNotification("Failed to add image. Try another file.");
      }
    }
  };

  const withPromptEnhancement = (hiddenPrompt: string) =>
    promptEnhancements ? `${PROMPT_ENHANCEMENT_TEXT}\n\n${hiddenPrompt}` : hiddenPrompt;

  return (
    <Flex direction={'column'} w="100%" pos='relative' gap={12}                         >

      {
        selectedModel === 'nanoBanana' && inpaintMode ? (
          <Flex w={'800px'} style={{ position: 'relative' }}>
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '6px',
                overflow: 'hidden'
              }}
            >
              <CanvasDraw
                ref={renderRef}
                backgroundColor='#00000028'
                lazyRadius={0}
                hideGrid={true}
                brushRadius={brushRadius}
                brushColor={`rgba(209,254,23,${brushOpacity})`}
                canvasHeight={canvasHeight}
                canvasWidth={canvasWidth}
                imgSrc={baseImageSrc}
              />
            </div>
          </Flex>
        ) : (
          <ImageObserved
            src={baseImageSrc}
            width="100%"
            height="auto"
            imgStyle={{ borderRadius: '6px', overflow: 'hidden' }}
          >
          </ImageObserved>
        )
      }
      <Flex
        style={{
          position: 'absolute',
          right: '-320px',
          top: '0',
          width: '300px',
          zIndex: 1000,
        }}
        gap={12}
        id="sidebar"
          direction="column"
      >
         <Flex
          direction="column"
          gap={12}
          w="100%"
          style={{
            backgroundColor: 'rgba(18,18,18,0.92)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Flex align="center" justify="space-between">
            <Text color="#fff" fz={16} fw={600}>
              Apply prompt enhancements
            </Text>
            <Switch
              size="md"
              color="orange"
              checked={promptEnhancements}
              onChange={(event) => setPromptEnhancements(event.currentTarget.checked)}
              aria-label="Toggle prompt enhancements"
            />
          </Flex>
          <Text color="rgba(255,255,255,0.6)" fz={12}>
            Adds a camera guidance paragraph to the start of the hidden prompt.
          </Text>
        </Flex>
        <Flex
          direction="column"
          gap={12}
          w="100%"
          style={{
            backgroundColor: 'rgba(18,18,18,0.92)',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Text color="#fff" fz={16} fw={600}>
            Change initial image
          </Text>
          <Text color="rgba(255,255,255,0.6)" fz={12}>
            Pick any backplate below to replace the starting scene.
          </Text>
          <ScrollArea h={520} type="auto" scrollHideDelay={200}>
            <Flex gap={8} wrap="wrap">
              {images.map((src) => {
                const isActive = src === baseImageSrc;
                return (
                  <button
                    key={src}
                    onClick={() => handleSelectBaseImage(src)}
                    style={{
                      width: '120px',
                      height: '90px',
                      borderRadius: '8px',
                      border: isActive ? '2px solid #e8590c' : '1px solid rgba(255,255,255,0.15)',
                      padding: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      boxShadow: isActive ? '0 0 12px rgba(232,89,12,0.35)' : 'none',
                    }}
                    aria-pressed={isActive}
                  >
                    <ImageObserved
                      src={src}
                      width="100%"
                      height="100px"
                      objectFit="cover"
                      imgStyle={{ objectFit: 'cover' }}
                      dark
                    />
                  </button>
                );
              })}
            </Flex>
          </ScrollArea>
        </Flex>
      </Flex>
      <PromptField
        credits={selectedModel === 'nanoBanana' ? computedCredits : baseCredits}
        onClick={async () => {
          const imageSrc = baseImageSrc;

          if (selectedModel === 'nanoBanana') {
            const srcset: string[] = [imageSrc, ...extraImages];
            if (inpaintMode && renderRef.current) {

              if (extraImages.length > 0) {
                try {
                  const composed = await composeWithBackground();
                  const dataUrl = composed || (renderRef.current as any).getDataURL('image/png', true);
                  const { url } = await runAsyncConvertBase64({ url: dataUrl }) as unknown as { url: string };
                  srcset.push(url);

                  const pipeline: PipelineParameters = {
                    provider: 'custom',
                    type: 'nanoBananaMultiple',
                    prompt: spaceStore.prompt,
                    hiddenPrompt: withPromptEnhancement(
                      `The first image shows the exact placement of the object. Integrate it seamlessly into the scene with photorealistic quality:
  - Remove the green brush from the image.
  - Do not change ratio of first backplate.
  - Match the exact lighting direction, intensity, and color temperature of the scene
  - Add realistic shadows that match the scene's lighting (cast shadows on surfaces, contact shadows at base)
  - Apply appropriate highlights and reflections based on the scene's light sources
  - Match the depth of field and focus of the background
  - Ensure perfect color harmony with the scene's ambient light and color grading
  - Blend edges naturally with soft transitions where the object meets the background
  - Preserve all surface textures and material properties realistically
  - Match the perspective and viewing angle of the scene
  Keep the object's position, size, and orientation exactly as shown in the guide.
  Addtitional prompt from user: ${spaceStore.prompt}`
                    ),

                    // "Using the provided images, place the elements onto there area drawed with green masked area. The added element should integrate into the image naturally. The ratio of background shoudn't change. " + spaceStore.prompt,
                    inputSrcs: [url, ...extraImages],
                    inputSrc: imageSrc,
                    credits: computedCredits,
                    size: selectedSize,
                    srcset,
                  };
                  await model.buy([pipeline]);
                } catch (e) {
                  // ignore overlay failure, proceed without it
                }
              } else {
                try {
                  const composed = await composeWithBackground();
                  const dataUrl = composed || (renderRef.current as any).getDataURL('image/png', true);
                  const { url } = await runAsyncConvertBase64({ url: dataUrl }) as unknown as { url: string };
                  srcset.push(url);

                  const pipeline: PipelineParameters = {
                    provider: 'custom',
                    type: 'nanoBananaMultiple',
                    prompt: spaceStore.prompt,
                    hiddenPrompt: withPromptEnhancement(
                      `Green masked area is the area that should be retouched. Prompt from user: ${spaceStore.prompt}`
                    ),
                    inputSrcs: [url, ...extraImages],
                    inputSrc: imageSrc,
                    credits: computedCredits,
                    size: selectedSize,
                    srcset,
                  };
                  await model.buy([pipeline]);
                } catch (e) {
                  // ignore overlay failure, proceed without it
                }
              }
            } else {
              if (srcset.length > 0) {
                const pipeline: PipelineParameters = {
                  provider: 'custom',
                  type: 'nanoBananaMultiple',
                  prompt: spaceStore.prompt,
                  hiddenPrompt: withPromptEnhancement('Addtitional prompt from user: ' + spaceStore.prompt),
                  inputSrcs: [imageSrc],
                  inputSrc: imageSrc,
                  credits: computedCredits,
                  size: selectedSize,
                  srcset,
                };
                await model.buy([pipeline]);
              } else {
                const pipeline: PipelineParameters = {
                  provider: 'custom',
                  type: 'nanoBanana',
                  prompt: spaceStore.prompt,
                  inputSrc: imageSrc,
                  credits: computedCredits,
                  size: selectedSize,
                  srcset,
                };
                await model.buy([pipeline]);
              }
            }

          } else {
            const pipeline: PipelineParameters = {
              provider: 'custom',
              type: 'placeCarK',
              prompt: spaceStore.prompt,
              inputSrc: imageSrc,
              backgroundSrc: imageSrc,
              placementSrc: imageSrc,
              credits: baseCredits,
              srcset: [],
            };
            await model.buy([pipeline]);
          }
        }}
        value={spaceStore.prompt}
        onChangeEnd={(value) => {
          spaceStore.setPrompt(value)
        }}
        loading={model.loading}
        placeholder="Describe an image and click generate"
      >
        <Text pl={18} fz={24} fw={700} color='#fff' lh={1.2} mb={2}>Upload images.<Tooltip
              label={
                <Text fw={500}>
                  Upload additional image elements and place them within the scene using the inpaint tool. Just mark the area with the brush and let the AI blend it naturally into your visual.
                </Text>
              }
              multiline
              w={320}
              withArrow
              position="bottom"
              color='dark'
            >
              <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" className="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
              </span>
            </Tooltip></Text>
        <Flex gap={2} p={10} wrap={'wrap'} pr={'100px'} w={'100%'}>
          {/* <Flex
            gap={8}
            align="center"
            style={{
              backgroundColor: 'rgba(32, 32, 32, 1)',
              borderRadius: '8px',
              padding: '8px',
              width: 'fit-content'
            }}
          >
            <Text color="white" size="sm" fw={500}>Model:</Text>
            <Flex gap={4}>
              <button
                onClick={() => setSelectedModel('nanoBanana')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: selectedModel === 'nanoBanana' ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                  color: 'white',
                  fontSize: '12px',
                  // fontWeight: selectedModel === 'nanoBanana' ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Nano Banana
              </button>
              <button
                onClick={() => setSelectedModel('kontext')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: selectedModel === 'kontext' ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                  color: 'white',
                  fontSize: '12px',
                  // fontWeight: selectedModel === 'kontext' ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Kontext
              </button>
            </Flex>
          </Flex> */}

          {selectedModel === 'nanoBanana' && (
            <Flex gap={4} align="center" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px' }}>
              <Text color="white" size="sm" fw={500}>Size:</Text>
              {(['1K', '2K', '4K'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: selectedSize === size ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '48px'
                  }}
                >
                  {size}
                </button>
              ))}
           
            </Flex>
          )}

          {
            selectedModel === 'nanoBanana' && (
              <Flex gap={16} align="center" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px' }}>
                <Text color="white" size="sm" fw={500}>Inpaint Tool:</Text>
                <button
                  onClick={() => {
                    setInpaintMode(!inpaintMode)
                    spaceStore.setPrompt('')
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: inpaintMode ? '#e8590c' : 'rgba(64, 64, 64, 1)',
                    color: 'white',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {inpaintMode ? 'On' : 'Off'}
                </button>
                {inpaintMode && (
                  <Flex gap={12} align="center">
                    <Flex direction="column" gap={4}>
                      <Text color="white" size="xs">Brush Size: {brushRadius}px</Text>
                      <input type="range" min={1} max={80} value={brushRadius} onChange={(e) => setBrushRadius(parseInt(e.currentTarget.value))} />
                    </Flex>
                    <Flex direction="column" gap={4}>
                      <Text color="white" size="xs">Opacity: {Math.round(brushOpacity * 100)}%</Text>
                      <input type="range" min={0} max={100} value={Math.round(brushOpacity * 100)} onChange={(e) => setBrushOpacity(parseInt(e.currentTarget.value) / 100)} />
                    </Flex>
                    <button
                      onClick={() => renderRef.current?.clear()}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        backgroundColor: 'transparent',
                        color: 'white',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>

                  </Flex>
                )}
              </Flex>
            )
          }

          {
            selectedModel === 'nanoBanana' && (
              <Flex gap={8} direction="column" style={{ backgroundColor: 'rgba(32, 32, 32, 1)', borderRadius: '8px', padding: '8px', width: '100%' }}>
                <Text color="white" size="sm" fw={500}>Additional images (up to 5)</Text>
                <Flex gap={8} wrap={'wrap'}>
                  {extraImages.map((src, idx) => (
                    <Flex key={src + idx} direction={'column'} gap={4} align={'center'} style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '6px', width: '120px', height: '140px', }}>
                      <ImageObserved width={'80px'} height={'80px'} dark src={src} objectFit="contain" />
                      <button
                        onClick={() => setExtraImages(extraImages.filter((_, i) => i !== idx))}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: 'rgba(64, 64, 64, 1)',
                          color: 'white',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </Flex>
                  ))}
                  {extraImages.length < 5 && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '120px', height: '140px', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)' }}>
                      + Add
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleAddExtraImages(e.currentTarget.files)}
                      />
                    </label>
                  )}
                </Flex>
              </Flex>
            )
          }
        </Flex>
      </PromptField>
    </Flex >
  );
};

export const WorkspaceAiRetouch = observer(Element);